// Shared Nivo theme + legend defaults so the Consumption charts match the
// axis, tick and legend styling used on the Emissions Insights charts
// (KpiCharts, EmissionsTrends).

export const chartTheme = {
  grid: { line: { stroke: "#EDF2F7", strokeWidth: 1 } },
  axis: {
    ticks: { text: { fontSize: 12, fill: "#718096" } },
    legend: { text: { fontSize: 13, fill: "#2D3748", fontWeight: 600 } },
  },
  legends: {
    text: { fontSize: 12, fontWeight: 600, fill: "#4A5568" },
  },
};

// Custom Nivo layer drawing a darker, slightly thicker line along the bottom
// of the plot area — the effective x-axis, wherever the y-scale minimum puts
// it. Add to a chart's `layers` array (after "axes"). Shared across the
// Insights line charts so they all anchor visually to the same baseline.
export const axisBaselineLayer = ({ innerWidth, innerHeight }) => (
  <line
    x1={0}
    x2={innerWidth}
    y1={innerHeight}
    y2={innerHeight}
    stroke="#718096"
    strokeWidth={2}
    strokeLinecap="square"
  />
);

// Right-hand vertical legend used alongside stacked bar / multi-line charts.
export const rightLegend = {
  anchor: "right",
  direction: "column",
  translateX: 120,
  itemsSpacing: 8,
  itemWidth: 100,
  itemHeight: 16,
  symbolSize: 10,
  symbolShape: "circle",
  itemTextColor: "#4A5568",
};

// Shared tooltip renderer for Nivo bar charts. Shows the keyed value and,
// when totalFor() resolves to a non-zero total, its percentage share.
// `totalFor(indexValue)` should return the sum of all keys at that column —
// this lets each chart define how totals are computed (per column, per
// stack, etc.) without making the helper guess.
// Line chart tooltip for a single hovered point. Percent is computed against
// the sum of all series at the same x-value.
export function makeLinePointTooltip(allSeries, unit = "") {
  // eslint-disable-next-line react/display-name
  return ({ point }) => {
    const xVal = point.data.x;
    const total = (allSeries || []).reduce((acc, s) => {
      const pt = s.data.find((d) => d.x === xVal);
      return acc + (pt?.y || 0);
    }, 0);
    const pct = total > 0 ? (point.data.y / total) * 100 : null;
    return (
      <div
        style={{
          background: "white",
          padding: "8px 12px",
          border: "1px solid #ccc",
          borderRadius: 4,
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          fontSize: 13,
          color: "#4A5568",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
          <span style={{ width: 10, height: 10, background: point.serieColor, borderRadius: 2, display: "inline-block" }} />
          <span style={{ fontWeight: 700, color: "#2D3748" }}>{point.serieId}</span>
        </div>
        <div>
          {Number(point.data.y).toLocaleString("en-GB", { maximumFractionDigits: 2 })}
          {unit ? ` ${unit}` : ""}
          {pct !== null && (
            <span style={{ color: "#A0AEC0", marginLeft: 8 }}>· {pct.toFixed(1)}%</span>
          )}
          <span style={{ color: "#A0AEC0", marginLeft: 8 }}>({xVal})</span>
        </div>
      </div>
    );
  };
}

export function makeBarTooltip(totalFor, unit = "") {
  // eslint-disable-next-line react/display-name
  return ({ id, value, color, indexValue }) => {
    const total = totalFor ? totalFor(indexValue) : 0;
    const pct = total > 0 ? (value / total) * 100 : null;
    return (
      // Chakra isn't available here (Nivo provides plain nodes); use inline styles.
      <div
        style={{
          background: "white",
          padding: "8px 12px",
          border: "1px solid #ccc",
          borderRadius: 4,
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          fontSize: 13,
          color: "#4A5568",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
          <span style={{ width: 10, height: 10, background: color, borderRadius: 2, display: "inline-block" }} />
          <span style={{ fontWeight: 700, color: "#2D3748" }}>{id}</span>
        </div>
        <div>
          {Number(value).toLocaleString("en-GB", { maximumFractionDigits: 2 })}
          {unit ? ` ${unit}` : ""}
          {pct !== null && (
            <span style={{ color: "#A0AEC0", marginLeft: 8 }}>· {pct.toFixed(1)}%</span>
          )}
        </div>
      </div>
    );
  };
}
